import { Autocomplete, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'shared/utils/toast';
import { IPurchaseOrder } from '../types';
import { orderService } from '../api/order.service';
import { EnhancedDialog } from 'components/enhanced';

interface SimulateInboundBollaDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function SimulateInboundBollaDialog({
  open,
  onClose,
  onSuccess
}: SimulateInboundBollaDialogProps) {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<IPurchaseOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<IPurchaseOrder | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    orderService
      .openOrdersAwaitingConfirmation()
      .then((res) => {
        setOrders(res.results || []);
      })
      .catch(() => {
        toast.error(t('pages.orders.delivery.simulate_bolla.load_orders_error'));
      });
  }, [open, t]);

  const handleClose = () => {
    setSelectedOrder(null);
    setFile(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedOrder || !selectedOrder.id || !file) {
      toast.error(t('pages.orders.delivery.simulate_bolla.validation_error'));
      return;
    }
    setLoading(true);
    try {
      await orderService.simulateInboundBolla(selectedOrder.id, file);
      toast.success(t('pages.orders.delivery.simulate_bolla.success'));
      onSuccess();
      handleClose();
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const message = err?.response?.data?.message || err?.message || '';
      toast.error(message || t('pages.orders.delivery.simulate_bolla.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <EnhancedDialog
      open={open}
      isValid={selectedOrder && file && !loading}
      onSubmit={handleSubmit}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      title={t('pages.orders.delivery.simulate_bolla.title')}>
      <Autocomplete
        options={orders}
        getOptionLabel={(option) => String(option.ref || '')}
        value={selectedOrder}
        onChange={(_event, value) => setSelectedOrder(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('pages.orders.delivery.simulate_bolla.order_label')}
            margin="normal"
            fullWidth
          />
        )}
      />
      <TextField
        type="file"
        fullWidth
        margin="normal"
        label={t('pages.orders.delivery.simulate_bolla.file_label')}
        InputLabelProps={{ shrink: true }}
        inputProps={{ accept: '.csv,.edi,.txt' }}
        onChange={({ target }) => setFile((target as HTMLInputElement).files?.[0] || null)}
      />
    </EnhancedDialog>
  );
}

export default SimulateInboundBollaDialog;
